"use client";

import { useMemo } from "react";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { cx } from "@/lib/tailwind-utils";
import { EmptyServicesState } from "./empty-services-state";
import { FlatServicesMenu } from "./flat-services-menu";
import { groupServicesByCategory } from "./helpers";
import { PriceDisclaimer } from "./price-disclaimer";
import { CategoryServiceAccordion } from "./service-accordion";
import { ServiceMenuItem } from "./service-menu-item";
import { useServicesView } from "./use-services-view";
export const MenuServicesView = ({
  className,
  onOpenDetails,
  onRequireMfa,
  onPhoneOnlyService,
  onOpenSelectionsDrawer
}: {
  className?: string;
  onOpenDetails?: (serviceId: string) => void;
  onRequireMfa?: (serviceId: string, onSelect?: (serviceId: string) => void) => void;
  onPhoneOnlyService?: (serviceId: string) => void;
  onOpenSelectionsDrawer?: (serviceId: string) => void;
}) => {
  const {
    frontPageData,
    filteredServices,
    isSearching,
    isLoading,
    selectedCategory,
    isServiceSelected,
    getServiceInstanceCount,
    isServiceUnavailable,
    selectedServiceIds,
    canSelectMore,
    effectiveNumAllowedServices,
    handleServiceClick,
    handleQuantityChange,
    handleInfoClick
  } = useServicesView({
    onOpenDetails,
    onRequireMfa,
    onPhoneOnlyService,
    onOpenSelectionsDrawer
  });
  const {
    blockingAppointment
  } = usePatientVerification();

  // Group services by category
  const shouldGroupByCategory = useMemo(() => {
    const backendFlag = frontPageData?.shouldGroupServicesByCategory;
    const hasCategories = (frontPageData?.categories?.length ?? 0) > 0;
    // Searching is a single ranked list, not configured category order.
    return !isSearching && backendFlag && hasCategories;
  }, [isSearching, frontPageData?.shouldGroupServicesByCategory, frontPageData?.categories]);
  const groupedServices = useMemo(() => {
    if (!shouldGroupByCategory) return [];
    return groupServicesByCategory(filteredServices ?? [], frontPageData?.categories);
  }, [shouldGroupByCategory, filteredServices, frontPageData?.categories]);
  if (!frontPageData || isLoading) return null;
  const showDuration = frontPageData.showServiceDuration !== false;
  const hasEstimatedPrices = filteredServices?.some(service => service.estimated);
  const getServiceMaxQuantity = (service_0: (typeof filteredServices)[number]) => {
    const selectionCount = getServiceInstanceCount(service_0.id);
    const remaining = Math.max(0, effectiveNumAllowedServices - selectedServiceIds.length);
    return service_0.allowMultipleBookings ? selectionCount + remaining : selectionCount;
  };
  const renderServiceItem = (service_1: (typeof filteredServices)[number], index: number, groupId: string, standalone: boolean, animationDelay = index * 30) => {
    const isSelected = isServiceSelected(service_1.id);
    const disabled = !!blockingAppointment || !isSelected && !service_1.onlyBookableOverThePhone && (!canSelectMore || isServiceUnavailable(service_1.id));
    return <ServiceMenuItem key={service_1.id + groupId} standalone={standalone} service={service_1} isSelected={isSelected} selectionCount={getServiceInstanceCount(service_1.id)} disabled={disabled} maxQuantity={getServiceMaxQuantity(service_1)} onServiceClick={handleServiceClick} onQuantityChange={handleQuantityChange} onInfoClick={handleInfoClick} showDuration={showDuration} animationDelay={animationDelay} />;
  };

  // Flat view (no grouping)
  if (!shouldGroupByCategory) {
    return <FlatServicesMenu services={filteredServices} className={className} renderService={(service_2, index_0, animationDelay_0) => renderServiceItem(service_2, index_0, selectedCategory ?? "", true, animationDelay_0)} />;
  }

  // Grouped view with collapsible categories
  return <div className={cx("mt-4", className)}>
      <CategoryServiceAccordion groups={groupedServices} renderService={renderServiceItem} emptyState={<EmptyServicesState />} />
      {hasEstimatedPrices && <PriceDisclaimer />}
    </div>;
};
export default MenuServicesView;